// @ts-check
import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import parseHTMLToReact from "html-react-parser";
import { Box, Modal, Button, Icon, Flex } from "orcs-design-system";
import {
  GradientBorder,
  Content,
  ToggleButton,
  CollapsibleContent,
  TypingIndicator,
} from "./StyledComponents";
import { AIWarning } from "./AIWarning";
import AIIcon from "./AI_Icon";

/**
 * @typedef {Object} AIMessageProps
 * @property {string | undefined} [content]
 * @property {Object} [options]
 * @property {boolean} [complete]
 * @property {boolean} [showAIWarning]
 * @property {string} [collapsible] - Controls collapsible behavior.
 *   - If not provided: No collapsible functionality (default behavior)
 *   - If "expanded": Shows collapsible button, defaults to expanded state
 *   - If "collapsed": Shows collapsible button, defaults to collapsed state
 * @property {boolean} [isCollapsed]
 * @property {Function} [onToggleCollapsed]
 */

/**
 * @param {AIMessageProps} props
 * @returns {import("react").JSX.Element | import("react").JSX.Element[]}
 */
export const AIMessage = ({
  content,
  options,
  complete,
  showAIWarning = true,
  collapsible,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle collapsible state internally
  // Note: collapsible prop controls whether the component shows collapsible functionality
  // - If collapsible is not provided: No collapsible button or behavior
  // - If collapsible="expanded": Shows button, defaults to expanded (visible content)
  // - If collapsible="collapsed": Shows button, defaults to collapsed (hidden content)
  const [isCollapsed, setIsCollapsed] = useState(collapsible === "collapsed");

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  // Safe HTML parsing with fallback
  const renderContent = () => {
    try {
      // Parse HTML directly since stripHTMLTags ensures only complete tags remain
      return parseHTMLToReact(content, options);
    } catch (error) {
      // Return plain text as fallback
      return <span>{content}</span>;
    }
  };

  const hasContent = (content?.length || 0) > 0;

  return (
    (hasContent && (
      <>
        <GradientBorder>
          <Content isCollapsed={isCollapsed}>
            {collapsible && (
              <ToggleButton
                onClick={toggleCollapsed}
                isCollapsed={isCollapsed}
                aria-label={
                  isCollapsed ? "Expand AI Summary" : "Collapse AI Summary"
                }
              >
                <Flex alignItems="center" gap="s">
                  <AIIcon width={20} height={19} />
                  AI Summary
                </Flex>
                <Icon
                  // @ts-ignore
                  icon={
                    isCollapsed
                      ? ["fas", "chevron-down"]
                      : ["fas", "chevron-up"]
                  }
                  size="xs"
                />
              </ToggleButton>
            )}
            <CollapsibleContent
              collapsible={collapsible}
              isCollapsed={isCollapsed}
            >
              {complete && content && content.includes("</graph>") && (
                <Flex justifyContent="flex-end">
                  {/** @ts-ignore */}
                  <Button
                    height="25px"
                    width="25px"
                    iconOnly
                    borderRadius="25px"
                    variant="ai"
                    onClick={() => setIsModalOpen(true)}
                    aria-label="Expand message"
                  >
                    <Icon
                      // @ts-ignore
                      icon={["fas", "expand-alt"]}
                      color="white"
                      size="sm"
                    />
                  </Button>
                </Flex>
              )}
              {/** @type {import("react").JSX.Element[]} */ renderContent()}
              {complete ? "" : <TypingIndicator />}
              {(showAIWarning || (complete && content)) && (
                <Flex justifyContent="space-between">
                  {showAIWarning && <AIWarning />}
                </Flex>
              )}
            </CollapsibleContent>
          </Content>
        </GradientBorder>

        <Modal
          visible={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          headerContent="AI Message"
          width="80vw"
          height="80vh"
          maxWidth="90vw"
          maxHeight="90vh"
        >
          {/** @ts-ignore */}
          <Box p="m">
            {/** @type {import("react").JSX.Element[]} */ renderContent()}
            {showAIWarning && <AIWarning />}
          </Box>
        </Modal>
      </>
    )) ||
    /** @type {import("react").JSX.Element[]} */ ([])
  );
};

AIMessage.propTypes = {
  content: PropTypes.string,
  options: PropTypes.any,
  complete: PropTypes.bool,
  showAIWarning: PropTypes.bool,
  collapsible: PropTypes.oneOf(["collapsed", "expanded"]),
};
